import { put, takeEvery, call } from 'redux-saga/effects'
import { AsyncAction } from 'src/utils/reduxUtils'
import {
  FetchContractorsPerCustomer,
  FetchContractorsAvailableForAssign,
  AssignContractorsToCustomer,
  UnassignContractorsFromCustomer,
} from 'src/constants/actionTypes'
import { Endpoints } from 'src/constants/endpoints'
import { ApiService, callSecureApi } from './api/callApi'
import { ContractorStatus } from 'src/constants/contractor/enums'
import { LinkableSubcontractor, LinkedSubcontractor } from 'src/interfaces/subcontractor'
import { Pagination } from 'src/interfaces/table'

interface FetchContractorsPerCustomerPayload {
  customerId?: string
  limit?: number
  offset?: number
  contractorStatus?: Array<ContractorStatus>
  contractorSearch?: string
  partnerMarketId?: string
  tags?: string[]
  sort?: string
}

function* onFetchContractorsPerCustomer(
  action: AsyncAction<
    FetchContractorsPerCustomerPayload,
    { data: LinkedSubcontractor[]; pagination: Pagination }
  >,
) {
  const {
    customerId,
    limit,
    offset,
    contractorStatus,
    contractorSearch,
    partnerMarketId,
    tags,
    sort,
  } = action.payload

  try {
    const query: any = {
      customerId,
      limit,
      offset,
      contractorStatus,
      contractorSearch,
      partnerMarketId,
      tags,
      sort,
    }

    const { payload }: { payload: { partnerDrivers: LinkedSubcontractor[] } } = yield call(
      callSecureApi,
      Endpoints.FetchContractorsPerCustomer,
      {
        method: 'GET',
        apiService: ApiService.Paraworks,
        query,
      },
    )

    const res = {
      data: payload.partnerDrivers,
      pagination: {
        total: payload.partnerDrivers.length,
        limit: payload.partnerDrivers.length,
        offset: 0,
      },
    }

    yield put(FetchContractorsPerCustomer.success(res))

    if (action.next) {
      action.next(null, res)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to fetch contractors per customer'

    yield put(FetchContractorsPerCustomer.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface FetchContractorsAvailableForAssignPayload {
  customerId?: string
  limit?: number
  offset?: number
  contractorStatus?: Array<ContractorStatus>
  contractorSearch?: string
  partnerMarketId?: string
  tags?: string[]
  sort?: string
}

function* onFetchContractorsAvailableForAssign(
  action: AsyncAction<
    FetchContractorsAvailableForAssignPayload,
    { data: LinkableSubcontractor[]; pagination: Pagination }
  >,
) {
  const {
    customerId,
    limit,
    offset,
    contractorStatus,
    contractorSearch,
    partnerMarketId,
    tags,
    sort,
  } = action.payload

  try {
    const query: any = {
      customerId,
      limit,
      offset,
      contractorStatus,
      contractorSearch,
      partnerMarketId,
      tags,
      sort,
    }

    const { payload }: { payload: { partnerDrivers: LinkableSubcontractor[] } } = yield call(
      callSecureApi,
      Endpoints.FetchContractorsAvailableForAssign,
      {
        method: 'GET',
        apiService: ApiService.Paraworks,
        query,
      },
    )

    const res = {
      data: payload.partnerDrivers,
      pagination: {
        total: payload.partnerDrivers.length,
        limit: payload.partnerDrivers.length,
        offset: 0,
      },
    }

    yield put(FetchContractorsAvailableForAssign.success(res))

    if (action.next) {
      action.next(null, res)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to fetch available contractors for assign'

    yield put(FetchContractorsAvailableForAssign.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface AssignContractorsToCustomerPayload {
  partnerDriverIds: string[]
  customerId: string
}

function* onAssignContractorsToCustomer(action: AsyncAction<AssignContractorsToCustomerPayload>) {
  const { partnerDriverIds, customerId } = action.payload

  try {
    yield call(callSecureApi, Endpoints.AssignContractorsToCustomer, {
      method: 'POST',
      apiService: ApiService.Paraworks,
      body: {
        partnerDriverIds,
        customerId,
      },
      returnResponse: true,
    })

    yield put(AssignContractorsToCustomer.success())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to assign contractors to customer'

    yield put(AssignContractorsToCustomer.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface UnassignContractorsFromCustomerPayload {
  selectedContractorIds: string[]
  customerId: string
}

function* onUnassignContractorsFromCustomer(
  action: AsyncAction<UnassignContractorsFromCustomerPayload>,
) {
  const { selectedContractorIds, customerId } = action.payload

  try {
    yield call(callSecureApi, Endpoints.UnassignContractorsFromCustomer, {
      method: 'POST',
      apiService: ApiService.Paraworks,
      body: {
        partnerDriverIds: selectedContractorIds,
        customerId,
      },
    })

    yield put(UnassignContractorsFromCustomer.success())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to unassign contractors from customer'

    yield put(UnassignContractorsFromCustomer.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

export function* linkSubcontractorSaga() {
  yield takeEvery(FetchContractorsPerCustomer.type.REQUEST, onFetchContractorsPerCustomer)
  yield takeEvery(
    FetchContractorsAvailableForAssign.type.REQUEST,
    onFetchContractorsAvailableForAssign,
  )
  yield takeEvery(AssignContractorsToCustomer.type.REQUEST, onAssignContractorsToCustomer)
  yield takeEvery(UnassignContractorsFromCustomer.type.REQUEST, onUnassignContractorsFromCustomer)
}
